import { Label, InputDate, AlertError, Text } from '@ohgoodlord/design-system'
import { ChangeEvent, useEffect } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { css } from '@emotion/react'
import { FormValues } from '..'
import { isTooYoung, Rules } from '../validation'
import { useUserData } from '../../../../../hooks/use-user-data'
import { logWarningToDatadog } from '../../../../../../common/utils'

const FIELD_NAME = 'dob'
const toInputValue = (isoDate?: string) => isoDate?.split('T')[0] ?? ''
const toFormValue = (inputValue: string) =>
  inputValue === '' ? '' : `${inputValue}T00:00:00.000Z`

const logIfTooYoung = ({
  isScottishResident,
  dob,
  logContext,
}: {
  isScottishResident: boolean
  dob?: string
  logContext: Record<string, unknown>
}) => {
  if (
    dob &&
    !Number.isNaN(new Date(dob).getTime()) &&
    isTooYoung(dob, isScottishResident)
  ) {
    logWarningToDatadog(
      `User has indicated that they are under ${
        isScottishResident ? '16' : '18'
      } years old`,
      { dob, ...logContext },
    )
  }
}

export const SubjectDetailsDobField = ({
  rules,
  isScottishResident,
}: {
  isScottishResident: boolean
  rules?: Rules<'dob'>
}) => {
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext<FormValues>()
  const { getUserData } = useUserData()

  const getLogContext = () => {
    const { userObj } = getUserData()
    return {
      subjectId: userObj?.id,
      applicationId: userObj?.applicationId,
      customerId: userObj?.customerId,
    }
  }

  useEffect(() => {
    logIfTooYoung({
      isScottishResident,
      dob: getValues(FIELD_NAME),
      logContext: getLogContext(),
    })
  }, [])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 5px;
      `}
    >
      <Label htmlFor={FIELD_NAME}>Date of birth</Label>
      <Controller
        name={FIELD_NAME}
        control={control}
        rules={rules}
        render={({ field: { value, onChange }, fieldState }) => (
          <InputDate
            value={toInputValue(value)}
            aria-required="true"
            isInvalid={!!fieldState.error?.message}
            aria-invalid={!!fieldState.error?.message}
            aria-describedby={fieldState.error ? `${FIELD_NAME}-error` : undefined}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const dob = toFormValue(e.target.value)
              onChange(dob)
              logIfTooYoung({ isScottishResident, dob, logContext: getLogContext() })
            }}
            name={FIELD_NAME}
            id={FIELD_NAME}
          />
        )}
      />
      {errors[FIELD_NAME] && (
        <div id={`${FIELD_NAME}-error`}>
          <AlertError>
            <Text>{errors[FIELD_NAME]?.message}</Text>
          </AlertError>
        </div>
      )}
    </div>
  )
}
